/**
 * PromptDetailView
 *
 * 2-column layout: selected version content (left) + version sidebar (right).
 * Clicking a version in the sidebar displays it on the left.
 * Drafts show editable content, live/archived show read-only with syntax highlighting.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlignJustify, Eye } from 'lucide-react';
import { Button } from '../../../../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../ui/tooltip';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '../../../../ui/breadcrumb';
import {
  usePromptVersions,
  usePromptDetail,
  useRenderedPrompt,
} from '../../hooks/usePrompts';
import VersionBadge from '../../shared/VersionBadge';
import PromptVersionSidebar from './PromptVersionSidebar';
import TemplateVariableHighlighter from './TemplateVariableHighlighter';
import CreatePromptModal from './CreatePromptModal';
import PromptPreviewPane from './PromptPreviewPane';

interface PromptDetailViewProps {
  promptKey: string;
  taxonomyKey: string;
  taxonomyDisplayName?: string;
  displayName: string;
  onEditDraft: (versionId: string) => void;
}

const PromptDetailView: React.FC<PromptDetailViewProps> = ({
  promptKey,
  taxonomyKey,
  taxonomyDisplayName,
  displayName,
  onEditDraft,
}) => {
  const navigate = useNavigate();
  const { data: versions } = usePromptVersions(promptKey, taxonomyKey);
  const liveVersion = versions?.find((v) => v.status === 'live');
  const totalVersions = versions?.length ?? 0;

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [createNewOpen, setCreateNewOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'raw' | 'preview'>('raw');

  const { data: selectedVersion } = usePromptDetail(selectedVersionId || '');
  const { data: rendered, isLoading: renderLoading, error: renderError } = useRenderedPrompt(
    viewMode === 'preview' && selectedVersionId ? selectedVersionId : ''
  );

  // Auto-select first version on load
  useEffect(() => {
    if (!selectedVersionId && versions && versions.length > 0) {
      setSelectedVersionId(versions[0].id);
    }
  }, [versions, selectedVersionId]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-6 rounded-2xl border border-border/70 bg-card p-6 shadow-[var(--shadow-card)]">
      {/* Breadcrumb Navigation */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink className="cursor-pointer" onClick={() => navigate('/v4/go-lab/intelligence/prompts')}>
              Prompts
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink className="cursor-pointer" onClick={() => navigate(`/v4/go-lab/intelligence/prompts?taxonomyKey=${taxonomyKey}`)}>
              {taxonomyDisplayName || taxonomyKey}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{displayName}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
        <p className="text-sm text-gray-500 mt-1">Manage prompt versions</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border bg-white p-4">
          <p className="text-sm text-gray-500">Live Version</p>
          <p className="text-2xl font-bold">
            {liveVersion ? `v${liveVersion.versionNumber}` : 'None'}
          </p>
          {liveVersion?.deployedAt && (
            <p className="text-xs text-gray-400 mt-1">
              Deployed {new Date(liveVersion.deployedAt).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-sm text-gray-500">Total Versions</p>
          <p className="text-2xl font-bold">{totalVersions}</p>
        </div>
      </div>

      {/* 2-column layout */}
      <div className="flex flex-1 min-h-0 gap-6 overflow-hidden">
        {/* Left: Selected version content */}
        <div className="flex-1 min-w-0 min-h-0">
          <div className="rounded-lg border bg-white p-6 h-full flex flex-col overflow-hidden">
            {/* Version header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-sm">
                  {selectedVersion ? `v${selectedVersion.versionNumber}` : 'No version selected'}
                </h3>
                {selectedVersion && <VersionBadge status={selectedVersion.status} />}
              </div>
              {selectedVersion && (
                <div className="flex items-center gap-2">
                  <TooltipProvider delayDuration={300}>
                    <div className="flex gap-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          variant={viewMode === 'raw' ? 'default' : 'ghost'}
                          className="h-7 w-7"
                          aria-label="View raw text"
                          onClick={() => setViewMode('raw')}
                        >
                          <AlignJustify className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Raw text</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          variant={viewMode === 'preview' ? 'default' : 'ghost'}
                          className="h-7 w-7"
                          aria-label="View preview"
                          onClick={() => setViewMode('preview')}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Rendered preview</TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
                </div>
              )}
            </div>

            {/* Content */}
            {selectedVersion ? (
              viewMode === 'preview' ? (
                <div className="flex-1 overflow-auto">
                  <PromptPreviewPane
                    sections={rendered?.sections}
                    isLoading={renderLoading}
                    error={(renderError as Error | null) ?? null}
                  />
                </div>
              ) : (
                <TemplateVariableHighlighter
                  content={selectedVersion.templateContent}
                  className="flex-1 overflow-auto"
                />
              )
            ) : !versions || versions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm mb-4">
                  No versions exist for this prompt yet.
                </p>
                <Button variant="outline" onClick={() => setCreateNewOpen(true)}>
                  Create First Version
                </Button>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Select a version from the sidebar.</p>
            )}
          </div>
        </div>

        {/* Right: Version sidebar */}
        <PromptVersionSidebar
          promptKey={promptKey}
          taxonomyKey={taxonomyKey}
          onEditDraft={onEditDraft}
          selectedVersionId={selectedVersionId}
          onSelectVersion={setSelectedVersionId}
        />
      </div>

      {/* Create First Version modal */}
      <CreatePromptModal
        taxonomyKey={taxonomyKey}
        open={createNewOpen}
        onOpenChange={setCreateNewOpen}
        existingPromptKey={promptKey}
        existingDisplayName={displayName}
      />
    </div>
  );
};

export default PromptDetailView;
